/**
 * The v2 incremental snapshot contract — verified live against the real,
 * deployed OBS-6/7/8 host on 2026-09-26
 * (https://futarchists.xyz/observatory/snapshots/fleet.json and
 * .../male-equity-founder-0001/index.json, both public/static). Every type
 * below is transcribed from an actual fetched response, not designed ahead
 * of the Python side — see `observatory/web/README.md` "Snapshot contract
 * v2 (incremental, live)" for the field-by-field notes and the one open
 * question (a `refs.*`-addressed object occasionally 404s/403s even right
 * after `index.json` names it — treated as "not published yet", see
 * `src/data/fetchJson.ts`).
 *
 * The old schema_version 1 contract (`src/types/snapshot.ts`'s
 * `SnapshotIndex`, one `<version>/` directory per publish) is kept as a
 * read fallback: nothing guarantees every role in the fleet has migrated at
 * once, and `RoleSnapshot` (`src/data/snapshotIndex.ts`) already picks by
 * `schema_version` at read time.
 */

// -- fleet.json ---------------------------------------------------------------

export interface FleetSnapshot {
  readonly schema_version: 2;
  readonly organisms: readonly FleetOrganismEntry[];
}

export interface FleetOrganismEntry {
  /** The directory name under the snapshot root, e.g.
   * "male-equity-founder-0001" — use this, not `role`, to build fetch paths. */
  readonly organism_id: string;
  /** The short role name ("male_equity"/"female_crypto") — use this for the
   * friendly-label mapping (`src/config.ts`'s `roleLabel`), never for paths. */
  readonly role: string;
  readonly started: boolean;
  readonly stage: string | null;
  readonly last_heartbeat_time: string | null;
  readonly nav: string | null;
  readonly drawdown: number | null;
  /** OQA: the systemd `is-active` word for the organism's unit ("active",
   * "inactive", "failed", ...). Optional: fleet.json published before OQA lacks it. */
  readonly unit_state?: string | null;
  /** OQA: `"pending_first_wake"` for a fresh episode whose unit has not run yet
   * (`index.json.episode`), else `null`/absent. */
  readonly episode_status?: string | null;
  /** OBS2: why the stage is what it is (e.g. "awaiting regime confirmation (1/3 days)"). */
  readonly stage_note?: string | null;
  /** D6: `true` while W sits below the capital-alert line (93% of opening capital),
   * `false` after a recovery, `null`/absent when no crossing was ever recorded. */
  readonly capital_alert?: boolean | null;
}

// -- index.json / latest.json --------------------------------------------------

export interface IncrementalChunkRef {
  readonly from_seq: number;
  readonly to_seq: number;
  readonly sha256: string;
  /** `false` for the current, still-growing chunk — its `events/<from>-
   * <to>.json` filename is unstable (the `to_seq` boundary moves every
   * publish); read `events/tail.json` for that region instead of trying to
   * fetch this chunk's own path (verified live: a `frozen: false` chunk's
   * exact filename 404s minutes later once the boundary has moved). */
  readonly frozen: boolean;
}

/** `{path, sha256}` — `path` is always `objects/<sha256>` (flat, no
 * `<sha256[:2]>` fan-out); both fields are given so a client never has to
 * compute it, but they are redundant with each other. */
export interface ContentAddress {
  readonly path: string;
  readonly sha256: string;
}

export interface AnatomyDataset {
  readonly dataset_version: string;
  readonly layout: ContentAddress;
  readonly taxonomy: ContentAddress;
}

export interface IncrementalIndex {
  readonly schema_version: 2;
  readonly organism_id: string;
  readonly role: string;
  readonly head_seq: number;
  readonly chunk_size: number;
  readonly chunks: readonly IncrementalChunkRef[];
  /** `null` = not published yet for that section (§ requirement: never
   * fabricate). Each non-null value is a sha256 to fetch at
   * `objects/<sha256>`. */
  readonly refs: {
    readonly equity_curve: string | null;
    readonly log_tail: string | null;
    readonly performance: string | null;
    readonly life: string | null;
    readonly family: string | null;
    /** Owner "resume" message (2026-09-26): an upcoming ref, absent from
     * every index.json verified live so far — optional so today's real
     * documents (which lack this key entirely) still satisfy the type. */
    readonly market?: string | null;
    /** R3 ("observatory visibility of smoother state") — optional for the
     * same reason `market` is: a role published before this stream landed
     * simply lacks the key. */
    readonly sizing?: string | null;
    /** BEN live benchmarks — optional: a role published before BEN lacks the key. */
    readonly benchmarks?: string | null;
  };
  /** Absent when the publisher ran without a layout root. */
  readonly anatomy?: {
    readonly tooltip_kb: ContentAddress;
    readonly mcns?: AnatomyDataset;
    readonly banc?: AnatomyDataset;
  };
  /** OQA: present only on a fresh episode's event-less stub (the organism has a
   * genesis record but has never run): its book is flat at `opening_capital`. */
  readonly episode?: IncrementalEpisode;
}

export interface IncrementalEpisode {
  /** `"pending_first_wake"` today. */
  readonly status: string;
  readonly created_utc: string | null;
  readonly opening_capital: string;
  readonly universe: readonly string[];
  readonly source: string;
}

export function isIncrementalIndex(index: { schema_version: number }): index is IncrementalIndex {
  return index.schema_version === 2;
}

export interface IncrementalLatestPointer {
  readonly head_seq: number;
  readonly published_at: string;
  readonly version: string;
}

// -- events/<from>-<to>.json / events/tail.json --------------------------------

/** Mirrors `organism.catalog.CatalogEvent` field-for-field (verified live). */
export interface IncrementalEvent {
  readonly seq: number;
  readonly event_id: string;
  readonly kind: string;
  readonly schema_version: string;
  readonly environment_id: string;
  readonly organism_id: string | null;
  readonly event_time: string;
  readonly knowledge_time: string;
  readonly recorded_time: string;
  readonly causal_parent_ids: readonly string[];
  readonly payload: Readonly<Record<string, unknown>>;
  readonly payload_digest: string;
}

export interface IncrementalEventChunkDocument {
  readonly from_seq: number;
  readonly to_seq: number;
  readonly events: readonly IncrementalEvent[];
}

// -- anatomy objects (fetched via ContentAddress.path) --------------------------

/** `{algorithm, digest, size}` — `organism.data.ArtifactRef`'s own shape,
 * used for the layout manifest's per-array pointers. Self-describing:
 * fetch `objects/<digest>` and parse as `.npy` (the dtype/shape live in the
 * npy header itself, verified live), no external metadata needed. */
export interface RawContentRef {
  readonly algorithm: "sha256";
  readonly digest: string;
  readonly size: number;
}

export interface LayoutManifest {
  readonly schema: string;
  // Known values are "mcns" | "banc", but typed as `string` (not a union
  // with those literals) because TS collapses `"mcns" | "banc" | string`
  // into `string` anyway (any other dataset id must still type-check).
  readonly dataset_id: string;
  readonly dataset_version: string;
  readonly generator_version: string;
  readonly manifest_sha256: string;
  readonly seed: number;
  readonly jitter_fraction: number;
  readonly neuron_count: number;
  readonly region_count: number;
  readonly class_count: number;
  readonly sensory_pool_count: number;
  readonly real_position_count: number;
  readonly real_position_fraction: number;
  readonly fallback_counts: Readonly<Record<string, number>>;
  readonly bounding_box_min: readonly [number, number, number];
  readonly bounding_box_max: readonly [number, number, number];
  readonly artifacts: {
    readonly xyz: RawContentRef;
    readonly region_id: RawContentRef;
    readonly class_id: RawContentRef;
    readonly sensory_pool_id: RawContentRef;
  };
}

export interface TaxonomyClassEntry {
  readonly class_id: number;
  readonly name: string;
  readonly color: string;
  readonly neuron_count: number;
}

export interface TaxonomyRegionEntry {
  readonly region_id: number;
  readonly name: string;
  readonly hemisphere: string;
  readonly color: string;
  readonly description: string;
  readonly neuron_count: number;
  readonly parent: number | null;
  /** Key into the *shared* `TooltipKbDocument.entries` map, or `null` if
   * this region has no curated structural tooltip yet — a real, honest
   * "not published yet" at the individual-region level. */
  readonly tooltip_kb_key: string | null;
}

export interface TaxonomySensoryPool {
  readonly slot: number;
  readonly symbol: string;
  readonly role: string;
  readonly neuron_count: number;
  readonly region_neuron_counts: Readonly<Record<string, number>>;
  readonly top_region_ids: readonly number[];
}

export interface TaxonomyDocument {
  readonly schema: string;
  readonly dataset_id: string;
  readonly dataset_version: string;
  readonly classes: readonly TaxonomyClassEntry[];
  readonly regions: readonly TaxonomyRegionEntry[];
  readonly sensory_pools: readonly TaxonomySensoryPool[];
}

export interface TooltipKbEntry {
  readonly display_name: string;
  readonly description: string;
  readonly dataset_scope: readonly string[];
}

export interface TooltipKbDocument {
  readonly schema: string;
  readonly entries: Readonly<Record<string, TooltipKbEntry>>;
}

// -- refs.*-addressed documents --------------------------------------------

/** Field list per the owner's live verification (2026-09-26); row shape
 * inside `rows` was not directly observable (every real role's
 * `refs.equity_curve` object 403/404'd at fetch time, see README) so it is
 * typed loosely and read defensively (`src/panels/PortfolioPanel.ts` never
 * assumes a field is present). */
export interface EquityCurveDocument {
  readonly rows: readonly Readonly<Record<string, unknown>>[];
}

export interface LogTailDocument {
  readonly lines: readonly unknown[];
}

export interface PerformanceDocument {
  readonly as_of_seq: number;
  readonly as_of_event_time: string;
  readonly nav: string;
  readonly economic_resources: string;
  readonly trading_cash: string;
  readonly positions_value: string;
  readonly unrealized_pnl: string;
  readonly realized_pnl_cum: string;
  readonly fees_broker_cum: string | null;
  readonly tariff_cum: string | null;
  readonly reward_log_growth: number | null;
  readonly drawdown: number | null;
}

export interface LifeProjection {
  readonly model: string;
  readonly median_cycles: number | null;
  readonly p10_cycles: number | null;
  readonly p90_cycles: number | null;
  readonly dominant_hazard: string;
  readonly economic_share: number | null;
  readonly natural_share: number | null;
  readonly censored_share: number | null;
  readonly inputs: Readonly<Record<string, unknown>>;
}

/** OBS2: W(t) against the economic-death floor (`life.economic_death_document`). */
export interface LifeEconomicDeath {
  readonly resources: string | null;
  readonly floor: string | null;
  /** `"life.assessment.solvency"` (the gate's own floor) | `"derived"` | `"derived_tariff_only"`. */
  readonly floor_source: string;
  readonly floor_rule: string;
  readonly tariff_floor: string;
  readonly capital_floor: string | null;
  readonly death_fraction: string | null;
  readonly opening_trading_capital: string | null;
  readonly distance: string | null;
  /** Fraction W(t) can fall before it reaches the floor: (W - floor) / W. */
  readonly fall_to_floor: number | null;
  /** False outside active/senescent: the runtime runs the gate only for a bound life. */
  readonly enforced: boolean;
}

/** OBS2: the brain's survival head as the estimate it is (`life.survival_head_document`). */
export interface LifeSurvivalHead {
  readonly model: string;
  readonly hazard: number | null;
  readonly hazard_ewma: number | null;
  readonly target: string;
  readonly drawdown_threshold: number;
  readonly used_in_projection: boolean;
  readonly calibration: {
    /** `"insufficient_data"` | `"calibrated"` | `"miscalibrated"`. */
    readonly state: string;
    readonly samples: number;
    readonly mean_predicted: number | null;
    readonly realized_rate: number | null;
    readonly brier: number | null;
    readonly min_samples: number;
    readonly tolerance: number;
  };
}

/** OBS2: regime-binding progress (`life.binding_document`). */
export interface LifeBinding {
  /** `"bound"` | `"awaiting_regime_confirmation"` | `"binding"` | `"ended"` | `"unknown"`. */
  readonly status: string;
  readonly rule: string;
  readonly entry_probability: number | null;
  readonly entry_confirmations: number | null;
  readonly startup_streak: number | null;
  readonly detector_started: boolean;
  readonly dominant_state: string | null;
  readonly dominant_posterior: number | null;
  readonly episode_id: string | null;
  readonly as_of: string | null;
}

/** D6: the latest `risk.capital_alert` crossing (alarm only — no trading change). */
export interface LifeCapitalAlert {
  /** `"breach"` (W fell below the line) | `"recovered"` (back above the recovery line). */
  readonly transition: string;
  readonly active: boolean;
  readonly resources: string;
  readonly opening_trading_capital: string;
  readonly capital_ratio: string;
  readonly alert_fraction: string;
  readonly recovery_fraction: string;
  readonly alert_line: string;
  readonly since: string | null;
  readonly event_time?: string;
  readonly note?: string;
}

export interface LifeDocument {
  readonly projection: LifeProjection;
  /** OQA: the latest `life.assessment` stage / sleep_state (null before one). */
  readonly stage?: string | null;
  readonly sleep_state?: string | null;
  /** OBS2 — optional: a life object published before OBS2 lacks these. */
  readonly economic_death?: LifeEconomicDeath;
  readonly survival_head?: LifeSurvivalHead;
  readonly binding?: LifeBinding;
  /** D6 — optional: null until the first crossing; absent before D6. */
  readonly capital_alert?: LifeCapitalAlert | null;
}

// -- market view (OBS-5, `refs.market`) --------------------------------------

/** Mirrors `organism.observatory.models.QuoteSnapshot` — an absent leg is
 * `null`, never fabricated (§A.4/OBS-5 requirement 1). */
export interface MarketQuote {
  readonly bid: string | null;
  readonly ask: string | null;
  readonly bid_size: string | null;
  readonly ask_size: string | null;
  /** BEN: the same quote at full precision (`bid`/`ask` are cent-rounded — a
   * sub-dollar coin reads "0.25 / 0.25"). Absent on events before BEN. */
  readonly bid_exact?: string | null;
  readonly ask_exact?: string | null;
}

/** Mirrors `organism.observatory.models.TradeSnapshot`. */
export interface MarketTrade {
  readonly price: string;
  readonly size: string;
  readonly trade_time: string;
  /** OQA: the print at full precision (`price` is cent-rounded). */
  readonly price_exact?: string | null;
}

/** Mirrors `organism.observatory.models.OrderbookLevel`. */
export interface MarketLadderLevel {
  readonly price: string;
  readonly size: string;
}

/** Mirrors `organism.observatory.models.LadderSnapshot` — top-of-book L2
 * depth, crypto feeds only (§A.4: equities have no depth to report). */
export interface MarketLadder {
  readonly bids: readonly MarketLadderLevel[];
  readonly asks: readonly MarketLadderLevel[];
}

/** One attended slot's latest folded `market.view` state (`MarketViewPayload`
 * verified live 2026-09-27 — `docs/OBSERVATORY_TELEMETRY.md` "Market view
 * (OBS-5)"). `bars_ref` is typed loosely (`ContentAddress`-shaped or absent)
 * — this frontend does not currently decode the bars npz artifact into a
 * chart (§A.4's candlestick overlay is a larger, separate lane); the panel
 * only needs to know a bars window exists to describe it honestly. */
export interface MarketSlot {
  readonly symbol: string;
  readonly feed: string;
  readonly session_state: string;
  readonly delay_ms: number | null;
  readonly latest_quote: MarketQuote | null;
  readonly last_trade: MarketTrade | null;
  readonly bars_ref: Readonly<Record<string, unknown>> | null;
  readonly bars_window_start: string | null;
  readonly bars_window_end: string | null;
  readonly gap: boolean;
  readonly staleness: number;
  readonly ladder: MarketLadder | null;
  readonly ladder_reason: string | null;
}

/** The `refs.market` object (`organism.observatory.publish._market_document`)
 * — every attended slot's latest state, sorted by symbol, plus the seq this
 * view is current as of. `slots` is `[]` (never absent/`null`) when this
 * role has not emitted `market.view` yet, verified live 2026-09-27. */
export interface MarketDocument {
  readonly as_of_seq: number;
  readonly slots: readonly MarketSlot[];
}

// -- sizing view (R3, `refs.sizing`) — observatory visibility of smoother state --

/** One slot symbol's current sizing state
 * (`organism.observatory.sizing_view.sizing_document`,
 * `docs/OBSERVATORY_TELEMETRY.md` "Sizing view (R3)"). Every field is folded
 * VERBATIM from R1's own `sizing.decision`/`sizing.config` catalog events —
 * no reconstruction, no recomputation (R3 pivot, post R1-merge). `
 * decision_detail` is a small, decision-specific bag built only from the
 * fields that one `sizing.decision` event itself carried for that outcome:
 * `{side, quantity}` for `"traded"`, `{side, reason}` for `"rejected"`,
 * `{reason}` for `"suppressed"` (and any outcome where the producer recorded
 * one), `{wakes_remaining}` for `"cooldown_blocked"`, `{delta, band}` for
 * `"held_in_band"` — read only the keys relevant to `decision`, never assume
 * the others exist. */
export interface SizingSlot {
  readonly symbol: string;
  readonly raw_signal: number | null;
  readonly smoothed_signal: number | null;
  readonly raw_history: readonly number[];
  readonly smoothed_history: readonly number[];
  readonly position_quantity: string | null;
  readonly mark_price: string | null;
  readonly mark_stale: boolean;
  /** `target_notional / equity` at the time of the last `sizing.decision`
   * this symbol carried a trace for — `null` whenever that decision had no
   * trace (a `"gapped"`/`"no_mark"`/`"suppressed"` outcome, or equity was not
   * yet known). */
  readonly target_weight: number | null;
  readonly current_weight: number | null;
  readonly cap_weight: number;
  readonly cooldown_wakes_remaining: number | null;
  readonly decision:
    | "traded"
    | "held_in_band"
    | "cooldown_blocked"
    | "rejected"
    | "gapped"
    | "no_mark"
    | "suppressed";
  readonly decision_detail: Readonly<Record<string, unknown>>;
  readonly decision_tick: number | null;
  readonly as_of_event_time: string | null;
  /** OQA: the risk allocation the last SIZED decision ran under, verbatim from
   * `sizing.decision.risk` (`organism.integration.risk.RiskTarget.payload`):
   * decimal strings. `null`/absent before one (or on a pre-RSK catalog). */
  readonly risk?: SizingRisk | null;
  /** The decision tick `risk` was sized at (a gapped wake keeps the last one). */
  readonly risk_tick?: number | null;
}

export interface SizingRisk {
  /** `"core_tilt"` or `"graded"`. */
  readonly policy?: string;
  /** core_tilt: the name's equal-weight core weight (fraction of W). */
  readonly base_weight?: string;
  /** core_tilt: `tilt_range * s`; the multiplier is `clip(1 + tilt, 0, 2)`. */
  readonly tilt?: string;
  /** core_tilt: the FINAL target as a fraction of W (after caps, pro rata,
   * build-in ramp and any turnover rationing). */
  readonly target_weight?: string;
  readonly target_notional?: string;
  /** core_tilt: build-in ramp 0..1 (the core is phased in over ~1 day). */
  readonly build_ramp?: string;
  readonly graded_strength?: string;
  readonly drawdown?: string;
  readonly survival_level?: string;
  readonly vol_scale?: string;
  readonly prorata_scale?: string;
  readonly band_fraction?: string;
  readonly round_trip_cost?: string;
  readonly gross_cap?: string;
  readonly tilt_ema_span?: number;
}

/** The `refs.sizing` object
 * (`organism.observatory.publish._sizing_document`) — every tracked slot's
 * latest sizing state, sorted by symbol, plus the book-level gross exposure
 * against `gross_cap_weight`. `slots` is `[]` (never absent/`null`) when this
 * role has never recorded a `sizing.decision` event for any symbol. */
export interface SizingDocument {
  readonly as_of_seq: number;
  readonly current_tick: number | null;
  readonly cap_weight: number;
  readonly gross_cap_weight: number;
  readonly gross_current_weight: number | null;
  /** `"reported"` once a real `sizing.config` event has been folded, else
   * `"assumed-defaults"` — the caps above came from this module's own
   * default constants, never the live loop's actual knobs. The UI shows a
   * small "settings not yet reported" note whenever this is
   * `"assumed-defaults"`. */
  readonly config_source: "reported" | "assumed-defaults";
  /** OQA: the policy of the latest sized decision and its build-in ramp. */
  readonly policy?: string | null;
  readonly build_ramp?: string | null;
  readonly slots: readonly SizingSlot[];
}

// -- live benchmarks (BEN, `refs.benchmarks`) ----------------------------------

/** The five benchmark series, in the fixed legend/colour order. */
export type BenchmarkSeriesName =
  "live" | "buy_and_hold" | "buy_and_hold_monthly" | "core_only" | "cash";

export interface BenchmarkSeriesStats {
  readonly final_value: number | null;
  readonly total_return: number | null;
  readonly daily_return: number | null;
  readonly realized_vol: number | null;
  readonly sharpe: number | null;
  readonly max_drawdown: number | null;
}

export interface BenchmarkRelative {
  readonly excess_return: number | null;
  readonly tracking_error: number | null;
  readonly information_ratio: number | null;
  readonly days: number;
  readonly upside_capture?: number | null;
  readonly downside_capture?: number | null;
}

export interface BenchmarkCheck {
  readonly name: string;
  readonly passed: boolean | null;
  readonly detail: string;
  readonly value?: number | null;
}

export type BenchmarkVerdict = "pass" | "fail" | "insufficient_data";

export interface BenchmarkGate {
  readonly status: BenchmarkVerdict;
  readonly provisional?: boolean;
  readonly window_days: number;
  readonly window_start?: string | null;
  readonly window_end?: string | null;
  readonly counted_days_in_window?: number;
  readonly counted_days_since_genesis?: number;
  readonly min_days?: number;
  readonly checks: readonly BenchmarkCheck[];
  /** Gate version ("v2" since the owner decision of 2026-10-11; absent = v1). */
  readonly version?: string;
  /** Reported, never judged (v2: the v1 one-month Sharpe comparison). */
  readonly informational?: readonly BenchmarkCheck[];
  /** Criterion 4 v2 since genesis (`organism.backtest.gate.Gate4V2Result.as_dict`). */
  readonly gate4_v2?: BenchmarkGate4V2;
}

export interface BenchmarkGate4V2 {
  readonly status: BenchmarkVerdict;
  readonly days: number;
  readonly min_days: number;
  readonly alpha_annual: number | null;
  readonly alpha_t: number | null;
  readonly beta: number | null;
  readonly beta_up: number | null;
  readonly beta_down: number | null;
  readonly convexity_t: number | null;
  readonly excess_sharpe: number | null;
  readonly bh_excess_sharpe: number | null;
  readonly rf_annual_mean: number | null;
  readonly rf_stale_days?: number | null;
}

export interface BenchmarkRegimeDay {
  readonly date: string;
  readonly organism: string;
  readonly rule: string;
  readonly live_return: number | null;
  readonly buy_and_hold_return: number | null;
}

export interface BenchmarkRegimeSegment {
  readonly days: number;
  readonly live_return: number | null;
  readonly buy_and_hold_return: number | null;
  readonly excess_return: number | null;
  readonly pct_positive_days: number | null;
  readonly worst_day: number | null;
}

export interface BenchmarkConsistency {
  readonly days: number;
  readonly weeks: number;
  readonly pct_positive_days: number | null;
  readonly pct_positive_weeks: number | null;
  readonly worst_day: { readonly date: string; readonly return: number | null } | null;
  readonly worst_week: { readonly week: string; readonly return: number | null } | null;
}

export type BenchmarkSeries = { readonly t: readonly string[] } & Readonly<
  Record<BenchmarkSeriesName, readonly (number | null)[]>
>;

/** The `refs.benchmarks` object (`organism.benchmark.metrics.build_benchmark_report`,
 * `docs/OBSERVATORY_TELEMETRY.md` "Benchmarks (BEN)"). A `status` other than `"ok"`
 * carries only the identity fields (and `genesis` once known). */
export interface BenchmarksDocument {
  readonly schema_version: 1;
  readonly organism_id: string;
  readonly role: string;
  readonly asset_class: string;
  readonly as_of_seq: number;
  readonly as_of_event_time: string | null;
  readonly status: "ok" | "no_genesis" | "no_samples";
  readonly genesis?: {
    readonly event_time: string | null;
    readonly source: string | null;
    /** Which event `event_time` is: the episode's first event (`session_started`,
     * OBS2), else its first `portfolio.snapshot` / the account opening. */
    readonly time_source?: string | null;
    readonly opening_capital: string | null;
    readonly universe: readonly string[];
    readonly universe_source: string;
    readonly gross_cap: string;
  };
  readonly days_observed?: number;
  readonly calendar_days?: number;
  readonly insufficient_data?: boolean;
  readonly min_days?: number;
  readonly series?: BenchmarkSeries;
  readonly stats?: Readonly<Record<BenchmarkSeriesName, BenchmarkSeriesStats>>;
  readonly relative?: Readonly<Record<Exclude<BenchmarkSeriesName, "live">, BenchmarkRelative>>;
  readonly consistency?: {
    readonly live: BenchmarkConsistency;
    readonly buy_and_hold: BenchmarkConsistency;
  };
  readonly costs?: {
    readonly fees_paid: string;
    readonly spread_paid: string;
    readonly tariff_paid: string;
    readonly traded_notional: string;
    readonly fills: number;
    readonly turnover_per_30d: number | null;
    readonly fees_spread_per_30d: number | null;
  };
  readonly tilt?: {
    readonly active: boolean;
    readonly contribution: number | null;
    readonly implementation_gap: number | null;
    readonly last_nonzero_time: string | null;
  };
  readonly data_quality?: {
    readonly coarse_quote_symbols: readonly string[];
    readonly spread_excluded_fills: number;
    readonly spread_unpriced_fills: number;
  };
  readonly core_only_reference?: {
    readonly wakes: number;
    readonly build_wakes: number;
    readonly build_ramp: number | null;
  };
  readonly regimes?: {
    readonly days: readonly BenchmarkRegimeDay[];
    readonly organism: Readonly<Record<string, BenchmarkRegimeSegment>>;
    readonly rule: Readonly<Record<string, BenchmarkRegimeSegment>>;
  };
  readonly gate?: BenchmarkGate;
  readonly scorecard?: {
    readonly source: string;
    readonly note: string;
    readonly status: BenchmarkVerdict;
    readonly checks: readonly BenchmarkCheck[];
  };
  /** P2 SCORE: the live forecast scorecard (absent on documents published before it). */
  readonly forecast_skill?: ForecastSkillDocument;
  /** OBS-REL: edge vs a same-size market basket (absent on documents published before it). */
  readonly market_relative?: MarketRelativeDocument;
}

/** One additive part of the fly's total return since genesis (fraction of opening capital). */
export interface MarketRelativeComponent {
  /** `core` | `tilt` | `rf` | `costs` | `tariff` (unknown keys render by label). */
  readonly key: string;
  readonly label: string;
  readonly value: number | null;
  /** Only the tilt is ever edge; the rest is market, risk-free or cost. */
  readonly edge: boolean;
  /** `"rf"` on the T-bill sleeve: risk-free, never edge. */
  readonly tag?: string;
}

/** `refs.benchmarks.market_relative` (`organism.benchmark.relative.build_market_relative`). */
export interface MarketRelativeDocument {
  readonly status: "ok" | "insufficient_data";
  readonly reason?: string;
  readonly days: number;
  readonly min_days: number;
  readonly tilt_active: boolean;
  readonly headline?: {
    readonly excess_since_genesis: number | null;
    readonly excess_30d: number | null;
    readonly window_days: number;
    readonly window_start: string;
    readonly window_counted_days: number;
    readonly tracking_error: number | null;
    readonly excess_vs_tbills_since_genesis: number | null;
    readonly excess_vs_tbills_30d: number | null;
  };
  readonly totals?: {
    readonly live: number | null;
    readonly basket: number | null;
    readonly tbills: number | null;
    readonly core_only: number | null;
  };
  readonly gross?: { readonly latest: number | null; readonly average: number | null };
  readonly series?: {
    readonly t: readonly string[];
    readonly excess_vs_basket: readonly (number | null)[];
    readonly excess_vs_tbills: readonly (number | null)[];
  };
  readonly decomposition?: {
    readonly total: number | null;
    readonly components: readonly MarketRelativeComponent[];
    readonly sum: number | null;
  };
  readonly rf_source?: string | null;
  readonly rf_error?: string | null;
}

/** One symbol's slice of a (window, horizon) forecast-skill block. */
export interface ForecastSkillSymbol {
  readonly n: number;
  readonly ic_pearson_pooled: number | null;
  readonly coverage_50: number | null;
  readonly coverage_90: number | null;
  readonly pinball_skill: number | null;
}

/** `forecast_skill.windows[w].horizons[h]` (`organism.forecast_eval.horizon_metrics`);
 * every statistic is `null` until the horizon has enough realized pairs. */
export interface ForecastSkillHorizon {
  readonly status: "ok" | "insufficient_data";
  readonly n: number;
  readonly wakes: number;
  readonly ic_rank_xs: number | null;
  readonly ic_rank_xs_positive: number | null;
  readonly ic_pearson_xs: number | null;
  readonly ic_rank_pooled: number | null;
  readonly ic_pearson_pooled: number | null;
  readonly rank_blocks: number;
  readonly coverage_50: number | null;
  readonly coverage_90: number | null;
  readonly coverage_by_level: Readonly<Record<string, number | null>> | null;
  readonly pinball: number | null;
  readonly pinball_naive: number | null;
  readonly pinball_skill: number | null;
  readonly per_symbol: Readonly<Record<string, ForecastSkillSymbol>>;
}

export interface ForecastSkillWindow {
  readonly from: string;
  readonly to: string;
  readonly days_with_data: number;
  readonly horizons: Readonly<Record<string, ForecastSkillHorizon>>;
}

export interface ForecastSkillForecaster {
  readonly digest: string;
  readonly trained?: boolean | null;
  readonly wakes?: number;
  readonly first?: string;
  readonly last?: string;
}

/** `refs.benchmarks.forecast_skill` (`organism.benchmark.forecast_skill`,
 * `docs/OBSERVATORY_TELEMETRY.md` "Forecast skill (P2 SCORE)"). */
export interface ForecastSkillDocument {
  readonly schema: string;
  readonly status: "ok" | "insufficient_data" | "no_forecasts";
  readonly message?: string | null;
  readonly as_of: string | null;
  readonly horizons_bars: readonly number[];
  readonly bar_seconds: number;
  /** The digest whose forecasts `windows` score (never blended with earlier ones). */
  readonly current_forecaster?: string | null;
  /** Earlier digests still inside the retention, scored separately (30-day window). */
  readonly previous_forecasters?: readonly {
    readonly digest: string;
    readonly windows: Readonly<Record<string, ForecastSkillWindow>>;
  }[];
  /** Cumulative since the scorecard's fold started (not windowed). */
  readonly counts: Readonly<Record<string, number>>;
  readonly skipped: Readonly<Record<string, number>>;
  readonly dropped: Readonly<Record<string, number>>;
  readonly forecasters: readonly ForecastSkillForecaster[];
  readonly windows: Readonly<Record<string, ForecastSkillWindow>> | null;
  readonly methodology?: Readonly<Record<string, string>>;
}
